import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { OpportunitesTabs } from "@/components/TalentSectionTabs";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { VerifiedBadge, RatingPill } from "@/components/Badges";
import { MapPin, Calendar, ArrowRight, Euro, Images, Heart, Crown, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PROJECT_TYPES = [
  { value: "all", label: "Tous" },
  { value: "shooting", label: "Shooting" },
  { value: "casting", label: "Casting" },
  { value: "mode", label: "Mode" },
  { value: "beaute", label: "Beauté" },
];

export default function Collaborations() {
  const { user } = useAuth();
  const [collabs, setCollabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    project_type: "all", location: "", paid: false,
    verified_only: false, premium_only: false,
  });
  const [favorites, setFavorites] = useState(new Set());
  const [quota, setQuota] = useState(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  useEffect(() => {
    if (user?.role === "talent") {
      api.get("/applications/quota").then(({ data }) => setQuota(data)).catch(() => {});
      api.get("/talent/favorite-collabs").then(({ data }) => setFavorites(new Set(Array.isArray(data) ? data : []))).catch(() => {});
    }
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: "active" };
      if (filters.project_type && filters.project_type !== "all") params.project_type = filters.project_type;
      if (filters.location) params.location = filters.location;
      if (filters.paid) params.paid = true;
      if (filters.verified_only) params.verified_only = true;
      if (filters.premium_only) params.premium_only = true;
      const { data } = await api.get("/collaborations", { params });
      setCollabs(Array.isArray(data) ? data : []);
    } catch {
      setCollabs([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (e, collabId) => {
    e.preventDefault();
    e.stopPropagation();
    const isFav = favorites.has(collabId);
    try {
      if (isFav) {
        await api.delete(`/talent/favorite-collabs/${collabId}`);
        const next = new Set(favorites); next.delete(collabId);
        setFavorites(next);
        toast.success("Retiré des favoris");
      } else {
        await api.post(`/talent/favorite-collabs/${collabId}`);
        setFavorites(new Set(favorites).add(collabId));
        toast.success("Ajouté aux favoris");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const isTalent = user?.role === "talent";

  return (
    <AppShell>
      <div data-testid="collaborations-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        {isTalent ? (
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <OpportunitesTabs />
            {quota && <div className="mt-2"><QuotaBadge quota={quota} /></div>}
          </div>
        ) : (
          <>
            <p className="caption-label text-[#C68E59] mb-3">Opportunités</p>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mb-10">Collaborations</h1>
          </>
        )}

        {/* Filters */}
        <div className="card-lux p-6 mb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end" data-testid="filters-bar">
          <div>
            <Label className="caption-label">Type de projet</Label>
            <Select value={filters.project_type} onValueChange={(v) => setFilters({ ...filters, project_type: v })}>
              <SelectTrigger className="rounded-xl mt-2" data-testid="filter-project-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="caption-label">Localisation</Label>
            <Input
              placeholder="Paris, Lyon…"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              className="rounded-xl mt-2"
              data-testid="filter-location"
            />
          </div>
          <div className="flex items-center gap-3 pt-5">
            <Switch checked={filters.paid} onCheckedChange={(v) => setFilters({ ...filters, paid: v })} data-testid="filter-paid" />
            <Label className="caption-label m-0">Rémunéré</Label>
          </div>
          <div className="flex items-center gap-3 pt-5">
            <Switch checked={filters.verified_only} onCheckedChange={(v) => setFilters({ ...filters, verified_only: v })} data-testid="filter-verified" />
            <Label className="caption-label m-0">Marque vérifiée</Label>
          </div>
          <div className="flex items-center gap-3 pt-5">
            <Switch checked={filters.premium_only} onCheckedChange={(v) => setFilters({ ...filters, premium_only: v })} data-testid="filter-premium-only" />
            <Label className="caption-label m-0">Premium</Label>
          </div>
        </div>

        {/* Collaboration cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collabs.map((c) => {
            const cover = c.cover_image ? `${API}/img/${c.cover_image}?w=1600&q=86` : null;
            const extras = (c.gallery_images?.length || 0);
            const isFav = favorites.has(c.id);
            return (
              <Link
                to={`/collaborations/${c.id}`}
                key={c.id}
                className="card-lux overflow-hidden flex flex-col group relative"
                data-testid={`collab-card-${c.id}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-[#FAF3EA]">
                  {cover ? (
                    <img src={cover} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#B3A78C] text-xs">Pas d'image</div>
                  )}
                  <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-white/85 backdrop-blur text-[10px] tracking-widest uppercase text-[#1A1A1A]">
                    {c.project_type}
                  </span>
                  <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
                    {c.is_premium_only && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#1A1A1A] text-[#D4A574] text-[10px] uppercase tracking-wide">
                        <Crown className="w-3 h-3" /> Premium
                      </span>
                    )}
                    {c.paid && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#E7F0E3] text-[#3F6A39] text-[10px] uppercase tracking-wide border border-[#BFD4B7]">
                        <Euro className="w-3 h-3" /> Rémunéré
                      </span>
                    )}
                  </div>
                  {extras > 0 && (
                    <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur text-white text-[10px]">
                      <Images className="w-3 h-3" /> +{extras}
                    </span>
                  )}
                  {isTalent && (
                    <button
                      onClick={(e) => toggleFavorite(e, c.id)}
                      className={`absolute bottom-3 left-3 w-9 h-9 rounded-full flex items-center justify-center transition ${isFav ? "bg-[#1A1A1A] text-[#D4A574]" : "bg-white/85 backdrop-blur text-[#1A1A1A] hover:bg-white"}`}
                      data-testid={`favorite-btn-${c.id}`}
                      aria-label="Favori"
                    >
                      <Heart className={`w-4 h-4 ${isFav ? "fill-[#D4A574]" : ""}`} />
                    </button>
                  )}
                </div>

                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-serif text-2xl text-[#1A1A1A] mb-2 leading-tight">{c.title}</h3>
                  {c.image_caption ? (
                    <p className="text-sm italic text-[#6E4B2A] mb-3 line-clamp-2">« {c.image_caption} »</p>
                  ) : (
                    <p className="text-sm text-[#666] leading-relaxed mb-4 line-clamp-3">{c.description}</p>
                  )}

                  <div className="mt-auto space-y-2 text-sm text-[#666]">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5" />
                      {c.location}
                    </div>
                    {c.date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(c.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-4 mt-4 border-t border-[#EADDC9]">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <span className="text-sm font-semibold text-[#1A1A1A] truncate">{c.pro_name}</span>
                      {c.pro_verified && <VerifiedBadge />}
                      <RatingPill rating={c.pro_avg_rating || 0} count={c.pro_review_count || 0} />
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#1A1A1A] flex-shrink-0" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {!loading && collabs.length === 0 && (
          <div className="text-center py-16 text-[#666]">Aucune collaboration pour le moment.</div>
        )}
      </div>
      </div>
    </AppShell>
  );
}

function QuotaBadge({ quota }) {
  if (quota.is_premium) {
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1A1A1A] text-[#D4A574] text-xs font-medium" data-testid="quota-premium">
        <Sparkles className="w-3.5 h-3.5" /> Candidatures illimitées · Premium
      </div>
    );
  }
  const remaining = quota.remaining ?? 0;
  const used = quota.used_today ?? 0;
  const limit = quota.limit ?? 3;
  return (
    <Link
      to="/talent/premium"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FAF3EA] border border-[#E6C29A] text-[11px] text-[#1A1A1A] hover:bg-[#F5E6D0] transition"
      data-testid="quota-free"
    >
      <Crown className="w-3.5 h-3.5 text-[#C68E59]" />
      {used}/{limit} candidatures aujourd'hui ·
      <span className="text-[#A26F3E] font-semibold ml-1">Passez Premium</span>
    </Link>
  );
}
